import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import React from 'react';

import { GroupedSimpleTable } from './GroupedSimpleTable';
import { GroupedVirtuosoTable } from './GroupedVirtuosoTable';
import {
  RowWrapper,
  RowWrapperV2,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

interface GroupedTableV2Props<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined; // Returns null/undefined for ungrouped rows
  getItemId?: (data: T) => string | undefined;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For hierarchical data
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  activeRowIds?: Set<string>;
  onRowSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  rowWrapperV2?: RowWrapperV2<T>;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  getSubRows?: (row: T) => T[]; // For hierarchical data
  isGroupRow?: (data: T) => boolean; // Identifies if a row is a group parent
  useVirtuoso?: boolean; // Enable virtualization for large datasets
  processRows?: (rows: Row<T>[], isGroupRow?: (data: T) => boolean) => Row<T>[]; // Optional custom row processing
  /** Row ID to scroll into view after data loads (e.g. after a new group is created). */
  scrollToItemRowId?: string;
  expandOnRowClick?: boolean;
}

export const GroupedTable = <T extends Record<string, any>>(
  props: GroupedTableV2Props<T>
) => {
  const { useVirtuoso = false, scrollToItemRowId, ...restProps } = props;
  // Use virtuoso for large datasets
  if (useVirtuoso) {
    return (
      <GroupedVirtuosoTable
        {...restProps}
        scrollToItemRowId={scrollToItemRowId}
      />
    );
  }

  return (
    <GroupedSimpleTable {...restProps} scrollToItemRowId={scrollToItemRowId} />
  );
};
